"use client";
import {
  LoaderComponent,
  ModalComponent,
  PaperComponent,
  SnackbarComponent,
} from "@/components";
import { useRouter } from "@/i18n/routing";
import {
  EMAIL_TYPE,
  GET_PRODUCT_APPLICATION,
  MOENGAGE_EVENTS,
  PRODUCT_HANDLER,
  STAGE,
  STAGE_BASED_ROUTING,
  WEB_COLORS,
  WEB_EVENTS,
  WORK_DETAILS_SCHEMA,
} from "@/lib/constants";
import { useFooter } from "@/lib/context/FooterContext";
import { sendEventAnalytics } from "@/lib/hooks/eventAnalytics";
import useOtpTimer from "@/lib/hooks/otpTimerHandler";
import useStageErrorHandler from "@/lib/hooks/stageErrorHandler";
import {
  useGetApplicationQuery,
  useGetCustomerQuery,
  usePostWorkDetailsMutation,
  useSendEmailOtpMutation,
  useVerifyEmailOtpMutation,
  useVerifyEpfoMailMutation,
} from "@/lib/redux";
import moengage from "@moengage/web-sdk";
import { Stack, Typography } from "@mui/material";
import { useFormik } from "formik";
import { useTranslations } from "next-intl";
import { enqueueSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import VerifyEmailOtpModalFields from "./email-modal-fields";
import WorkDetailFields from "./work-details-fields";
import useErrorHandling from "@/lib/hooks/errorhandling";
import { AddressStoreMethods } from "@/components/Address";

const WorkDetailsScreen = ({ product }) => {
  const t = useTranslations("WorkDetails");
  const router = useRouter();
  const { setActions } = useFooter();
  const initialized = useRef(false);
  const { timeLeft, setTimeLeft, startTimer, resetTimer } = useOtpTimer(180);
  const [openOtpModal, setOpenOtpModal] = useState(false);
  // Tracks the email + type for which an OTP was last successfully generated,
  // so we can reuse a still-valid OTP instead of generating a new one.
  const [otpRequestedFor, setOtpRequestedFor] = useState({
    email: "",
    emailType: "",
  });
  /** --------------------------RTK QUERY-------------------------- **/

  const {
    data: applicationData,
    error: errorGetApplication,
    isLoading: applicationLoading,
    refetch: refetchApplicationData,
  } = useGetApplicationQuery("", {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });


  const productApplication = GET_PRODUCT_APPLICATION(
    PRODUCT_HANDLER[product],
    applicationData?.data,
  );
  const {
    data: customerData,
    error: errorGetCustomer,
    isLoading: customerLoading,
  } = useGetCustomerQuery();
  // Patch work details attributes to /application
  const [
    postWorkDetails,
    { error: errorWorkDetails, isLoading: workDetailsLoading },
  ] = usePostWorkDetailsMutation();
  // Sends OTP for both personal and work emails
  const [
    sendEmailOtp,
    {
      isLoading: otpGenerating,
      isSuccess: otpGenerated,
      error: sendEmailOtpError,
      reset: resetSendEmailOtp,
    },
  ] = useSendEmailOtpMutation();
  // Verifies OTP for both personal and work emails
  const [
    verifyEmailOtp,
    {
      isLoading: otpVerifying,
      isSuccess: verificationSuccess,
      error: verifyEmailOtpError,
      reset: resetVerifyEmailOtp,
    },
  ] = useVerifyEmailOtpMutation();
  // EPFO verification (only triggered after WORK email OTP is verified)
  const [
    verifyEpfoMail,
    {
      data: epfoVerificationData,
      error: epfoVerificationError,
      isLoading: epfoVerifying,
      reset: resetEpfoVerification,
    },
  ] = useVerifyEpfoMailMutation();

  useEffect(() => {
    setActions({
      showReject: true,
      showConfirm: true,
      onConfirm: formik.handleSubmit,
      confirmBtnLabel: "Proceed",
    });
  }, [setActions]);

  const isError =
    errorGetApplication ||
    errorGetCustomer ||
    errorWorkDetails ||
    sendEmailOtpError ||
    verifyEmailOtpError ||
    epfoVerificationError;
  const isLoading =
    applicationLoading ||
    customerLoading ||
    workDetailsLoading ||
    otpGenerating ||
    otpVerifying ||
    epfoVerifying;


  const formik = useFormik({
    enableReinitialize: !initialized.current,
    initialValues: {
      employmentType: productApplication?.employmentType || "",
      emailVerified: productApplication?.personalEmailId ? true : false,
      officialEmailVerified: productApplication?.workEmailId ? true : false,
      companyName: productApplication?.companyName || "",
      businessName: productApplication?.businessName || "",
      maritalStatus: "",
      education: "",
      purposeOfLoan: "",
      language: "",
      netAnnualIncome: "",
      personalEmailId: productApplication?.personalEmailId || "",
      workEmailId: productApplication?.workEmailId || "",
      aadharMobileNumber: "",
      line1:
        productApplication?.companyName ||
        productApplication?.businessName ||
        "",
      line2: "",
      line3: "",
      line4: "",
      line5: "",
      pin: "",
      city: "",
      state: "",
      landmark: "",
      country: "",
      companyAddress: "",
      emailType: "",
    },
    validationSchema: WORK_DETAILS_SCHEMA,
    onSubmit: (values) => {
      moengage.setEmailId(`${values?.personalEmailId}`);
      moengage.setUserAttribute("workEmailId", values?.workEmailId);
      const requestData = {
        appId: productApplication?.appId,
        customerId: productApplication?.customerId,
        ...values,
        netAnnualIncome: parseInt(values?.netAnnualIncome),
        companyName: values.companyName.toUpperCase(),
      };
      postWorkDetails(requestData);
      // reseting address Store when work details are submitted.
      AddressStoreMethods?.resetAddressStore();
    },
  });
  /** --------------------------ACTION-------------------------- **/
  const isPersonalEmail = formik.values.emailType === EMAIL_TYPE.PERSONAL;
  const isWorkEmail = formik.values.emailType === EMAIL_TYPE.WORK;

  const getEmailForCurrentType = () =>
    isWorkEmail ? formik.values.workEmailId : formik.values.personalEmailId;

  const handleFieldChange = (e, regex) => {
    if (regex(e.target.value)) {
      formik.handleChange(e);
    }
  };

  const handleOtpSubmit = async (otp) => {
    await verifyEmailOtp({
      appId: productApplication?.appId,
      email: getEmailForCurrentType(),
      otp,
      emailType: formik.values.emailType,
    });
  };

  // Opens the OTP verification modal. If an OTP was already generated for the
  // same (email, emailType) and the timer hasn't elapsed, we skip re-sending so
  // the user can enter the still-valid older OTP. Otherwise a fresh OTP is
  // generated and the modal is opened by the otpGenerated effect below.
  const handleVerifyEmailClick = (emailType) => {
    const email =
      emailType === EMAIL_TYPE.WORK
        ? formik.values.workEmailId
        : formik.values.personalEmailId;

    formik.setFieldValue("emailType", emailType);

    if (emailType === EMAIL_TYPE.PERSONAL) {
      moengage.track_event(
        MOENGAGE_EVENTS?.VERIFY_PERSONAL_EMAIL_OTP_MODAL_OPENED,
        {
          phone: customerData?.data?.phone,
          appId: productApplication?.appId,
        },
      );
    }

    const isSameRequest =
      otpRequestedFor.email === email &&
      otpRequestedFor.emailType === emailType;
    const otpStillValid = otpGenerated && timeLeft > 0 && isSameRequest;

    if (otpStillValid) {
      resetVerifyEmailOtp();
      setOpenOtpModal(true);
      return;
    }

    setOtpRequestedFor({ email, emailType });
    resetVerifyEmailOtp();
    resetEpfoVerification();
    sendEmailOtp({
      appId: productApplication?.appId,
      email,
      emailType,
    });
  };

  const handleResendOtp = () => {
    const email = getEmailForCurrentType();
    const emailType = formik.values.emailType;
    resetTimer();
    resetVerifyEmailOtp();
    resetEpfoVerification();
    setOtpRequestedFor({ email, emailType });
    sendEmailOtp({
      appId: productApplication?.appId,
      email,
      emailType,
    });
  };

  // Closes the OTP modal but intentionally does NOT reset the sendEmailOtp
  // state or the timer, so the user can re-open the modal and re-enter the
  // still-valid OTP without having to trigger a new send.
  const handleOtpModalClose = () => {
    setOpenOtpModal(false);
    resetVerifyEmailOtp();
    resetEpfoVerification();
    if (isPersonalEmail) {
      moengage.trackEvent(
        MOENGAGE_EVENTS?.VERIFY_PERSONAL_EMAIL_OTP_MODAL_CLOSED,
        {
          phone: customerData?.data?.phone,
          appId: productApplication?.appId,
        },
      );
    }
  };

  const handleVerificationSuccess = (fieldName, message) => {
    setOpenOtpModal(false);
    resetTimer();
    resetSendEmailOtp();
    setOtpRequestedFor({ email: "", emailType: "" });
    formik.setFieldValue(fieldName, true);
    SnackbarComponent({
      success: true,
      successMessage: message,
    });
  };

  useStageErrorHandler({
    stage: productApplication?.stage,
    prevStage: productApplication?.previousStage,
    message: productApplication?.loanReapplyMessage,
  });
  /** --------------------------EFFECT-------------------------- **/

  useEffect(() => {
    // initialize the ref once the product data is available, to handle edge cases
    if (productApplication?.appId) initialized.current = true;
  }, [productApplication?.appId]);

  useEffect(() => {
    if (customerData?.data && productApplication?.appId) {
      sendEventAnalytics({
        eventName: WEB_EVENTS.ON_WORK_DETAILS_STARTED,
        ...productApplication,
        ...customerData?.data,
      });
      refetchApplicationData();
    }
    if (productApplication?.previousStage === STAGE?.CREDIT_QUEUE) {
      enqueueSnackbar(
        "Verification Successful!. You can now continue with your loan application.",
        { variant: "success", autoHideDuration: 3000, preventDuplicate: true },
      );
    }
  }, [productApplication, customerData]);
  useEffect(() => {
    if (productApplication?.stage) {
      router.push(STAGE_BASED_ROUTING(productApplication?.stage));
    }
  }, [productApplication?.stage]);


  useEffect(() => {
    if (otpGenerated) {
      resetTimer();
      startTimer();
      setOpenOtpModal(true);
      if (isPersonalEmail) {
        moengage.trackEvent(MOENGAGE_EVENTS?.PERSONAL_EMAIL_OTP_GENERATED, {
          phone: customerData?.data?.phone,
          appId: productApplication?.appId,
        });
      }
    }
  }, [otpGenerated]);

  // Handle OTP verification success
  useEffect(() => {
    if (!verificationSuccess) return;
    if (isPersonalEmail) {
      handleVerificationSuccess("emailVerified", "Personal E-mail verified");
    } else if (isWorkEmail) {
      // EPFO check is only performed for WORK email
      verifyEpfoMail({
        appId: productApplication?.appId,
        workEmailId: formik.values.workEmailId,
        consent: "Y",
      });
    }
  }, [verificationSuccess]);

  // Handle EPFO verification success (only relevant for WORK email)
  useEffect(() => {
    if (epfoVerificationData?.data?.verified === true) {
      handleVerificationSuccess(
        "officialEmailVerified",
        "Official E-mail verified",
      );
    }
  }, [epfoVerificationData]);

  useErrorHandling(isError);

  useEffect(() => {
    if (isError) {
      if (isError?.status === 401) {
        return;
      }
      if (isError?.data?.message?.toLowerCase()?.includes("invalid")) {
        moengage.trackEvent(MOENGAGE_EVENTS?.INVALID_PERSONAL_EMAIL_OTP, {
          phone: customerData?.data?.phone,
          appId: productApplication?.appId,
        });
      }
      if (isError?.data?.timeLeft) {
        const newTimeLeft = isError?.data?.timeLeft;
        if (newTimeLeft) {
          setTimeLeft(newTimeLeft);
        }
      }
    }
  }, [isError]);
  return (
    <Stack justifyContent={"center"}>
      <LoaderComponent open={isLoading} />
      <PaperComponent
        paperStyle={{ backgroundColor: "rgba(242, 247, 247, 0.6)" }}
      >
        <Stack rowGap={2}>
          <Typography variant="h5" color={WEB_COLORS.primary} fontWeight={700}>
            {t("tittle")}
          </Typography>
          <WorkDetailFields
            formik={formik}
            timeLeft={timeLeft}
            {...productApplication}
            phone={customerData?.data?.phone}
            handleVerifyEmailClick={handleVerifyEmailClick}
            handleFieldChange={handleFieldChange}
            sendEmailOtpError={sendEmailOtpError}
          />
        </Stack>
      </PaperComponent>

      <ModalComponent open={openOtpModal} handleModal={handleOtpModalClose}>
        <VerifyEmailOtpModalFields
          formik={formik}
          timeLeft={timeLeft}
          otpVerifying={otpVerifying || epfoVerifying}
          handleOtpSubmit={handleOtpSubmit}
          handleResendOtp={handleResendOtp}
          error={verifyEmailOtpError || epfoVerificationError}
        />
      </ModalComponent>
    </Stack>
  );
};

export default WorkDetailsScreen;
